import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  Drawer,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Grid,
  Snackbar,
  Alert,
  Avatar,
  InputAdornment,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import BadgeIcon from "@mui/icons-material/Badge";
import ArchiveIcon from "@mui/icons-material/Archive";
import { Leaf, Flame } from "lucide-react";
import dayjs from "dayjs";
import { AuthContext } from "../context/AuthContext";
import UpdateOfferLetter from "./UpdateOfferLetter";
import EmployeePay from "./EmployeePay";
import DescriptionIcon from "@mui/icons-material/Description";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

// Format pay rate as currency with 2 decimals
const formatPay = (val) => {
  const n = Number(val);
  if (Number.isNaN(n)) return '$0.00';
  return `$${n.toFixed(2)}`;
};

  const Employees = ({ initialStore = "All" }) => {
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedStore, setSelectedStore] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [newSubject, setNewSubject] = useState("");
    const [newContent, setNewContent] = useState("");
    const [locations, setLocations] = useState([]);
    const [employmentFilter, setEmploymentFilter] = useState("active");
    // Desktop table sort (mobile keeps the card layout and is unsorted).
    const [sortBy, setSortBy] = useState("lastName");
    const [sortDir, setSortDir] = useState("asc");
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [employeeToEdit, setEmployeeToEdit] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [updateOfferLetterOpen, setUpdateOfferLetterOpen] = useState(false);
    const [employeeToUpdateOffer, setEmployeeToUpdateOffer] = useState(null);
    const [employeePayOpen, setEmployeePayOpen] = useState(false);
    const [employeeToUpdatePay, setEmployeeToUpdatePay] = useState(null);
    const [showSSN, setShowSSN] = useState(false);
    const [showBankAccount, setShowBankAccount] = useState(false);
    const [showRoutingNumber, setShowRoutingNumber] = useState(false);
    // Archive-from-profile (Karen, 2026-09-04: archiving previously lived only on
    // the Dashboard "Employee Actions" card, so the Employees page — where people
    // actually go to manage staff — offered no way to do it).
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [archiveReason, setArchiveReason] = useState("");
    const [archiveDate, setArchiveDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  
    const { userRole } = useContext(AuthContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const canEditEmployee = userRole === "admin" || userRole === "manager";

    // Define fetchEmployees outside of useEffect so it can be called from other places
    const fetchEmployees = useCallback(async () => {
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            throw new Error("No token found");
          }
      
          console.log("Token:", token); // Log the token
      
          let url = `${API_URL}/employees`;
      
          // Build query parameters
          const params = new URLSearchParams();
          
          // Include archived employees in the request
          params.append('includeArchived', 'true');
          
          // If the user is an admin and a store is selected, append the store query parameter
          const user = JSON.parse(localStorage.getItem("user"));
          if (user?.role === "admin" && selectedStore !== "All") {
            params.append('store', selectedStore);
          }
          
          if (params.toString()) {
            url += `?${params.toString()}`;
          }
      
          console.log("Fetching employees from:", url); // Log the URL
      
          const res = await fetch(url, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
      
          if (!res.ok) {
            const errorData = await res.json(); // Parse the error response
            console.error("Error response:", errorData);
            throw new Error(`Server error: ${res.status}`);
          }
      
          const data = await res.json();
          console.log("Employees data:", data); // Log the employees data
          setEmployeeList(data);
        } catch (err) {
          console.error("Failed to load employees:", err);
        }
    }, [selectedStore]);

    useEffect(() => {
      fetchEmployees();
    }, [selectedStore, fetchEmployees]); // Re-fetch employees when selectedStore changes

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/locations`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
  
        if (!res.ok) throw new Error("Failed to fetch locations");
        const data = await res.json();
        setLocations(data);
      } catch (err) {
        console.error("Failed to load locations:", err);
      }
    };
  
    fetchLocations();
  }, []);



  // Handle profile
  const handleViewProfile = async (employee) => {
    try {
      const token = localStorage.getItem("token");
      console.log('Starting profile fetch for employee:', employee.id);
      
      if (!token) {
        console.error("Authentication token not found.");
        return;
      }
  
      const timestamp = new Date().getTime();
      const url = `${API_URL}/employees/${employee.id}?t=${timestamp}`;

      console.log('Fetching from URL:', url);
  
      const res = await fetch(url, {
        headers: { 
          Authorization: `Bearer ${token}`
        },
        credentials: 'include'
      });
  
      console.log('Response status:', res.status);
  
      if (!res.ok) {
        const errorData = await res.json();
        console.error('Error response:', errorData);
        throw new Error(`Server error: ${res.status} - ${errorData.error || res.statusText}`);
      }
  
      const detailedEmployee = await res.json();
      console.log('Received employee details:', detailedEmployee);
  
      setSelectedEmployee(detailedEmployee);
      if (detailedEmployee.notes) {
        setNotes(detailedEmployee.notes.map((note) => ({ ...note, expanded: false })));
      } else {
        setNotes([]);
      }
  
      setIsProfileOpen(true);
    } catch (err) {
      console.error("Error in handleViewProfile:", err);
      if (err.message) {
        console.error("Error details:", err.message);
      }
    }
  };

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
    setSelectedEmployee(null);
    setNotes([]);
    // Reset sensitive data visibility
    setShowSSN(false);
    setShowBankAccount(false);
    setShowRoutingNumber(false);
  };

  const handleOpenArchiveDialog = () => {
    setArchiveReason("");
    setArchiveDate(new Date().toISOString().slice(0, 10));
    setArchiveDialogOpen(true);
  };

  // Archive the employee currently open in the profile drawer.
  // POST /employees/archive requires { employeeId, archiveDate (ISO8601), reason }
  // and is gated server-side to admin/manager; managers may only archive within
  // their own location, so a 403 here is expected for a cross-location attempt.
  const handleArchiveEmployee = async () => {
    if (!selectedEmployee || !archiveReason.trim()) return;
    setArchiveSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/employees/archive`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          employeeId: selectedEmployee.id,
          // Send YYYY-MM-DD, exactly as the Dashboard batch flow does. The route
          // validator accepts any ISO-8601, but the `fireDate` column rejects a
          // full timestamp: sending one fails with ER_TRUNCATED_WRONG_VALUE
          // ("Incorrect date value: '2026-09-04T19:34:19.154Z'") and the request
          // 500s. Verified against staging 2026-09-04.
          archiveDate,
          reason: archiveReason.trim(),
        }),
      });
      if (!res.ok) {
        // Surface the server's own message (e.g. the manager cross-location
        // refusal) rather than a generic failure.
        let detail = `Server error: ${res.status}`;
        try {
          const body = await res.json();
          if (body?.error) detail = body.error;
        } catch { /* non-JSON error body — keep the status */ }
        throw new Error(detail);
      }
      const name = `${selectedEmployee.firstName} ${selectedEmployee.lastName}`;
      setArchiveDialogOpen(false);
      handleCloseProfile();
      await fetchEmployees();
      setSnackbarMessage(`${name} has been archived.`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error archiving employee:", err);
      setSnackbarMessage(err.message || "Could not archive this employee.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setArchiveSubmitting(false);
    }
  };

  // Function to mask sensitive data
  const maskSensitiveData = (data, type) => {
    if (!data) return "Not provided";
    
    if (type === 'ssn') {
      // Show all asterisks for SSN
      return "***-**-****";
    } else if (type === 'account') {
      // Show all asterisks for account number
      return "**********";
    } else if (type === 'routing') {
      // Show all asterisks for routing number
      return "*********";
    }
    return "****";
  };

  // Handle edit employee button click
  const handleEditEmployee = (employee) => {
    setEmployeeToEdit(employee);
    setEditFormData({
      name: employee.name,
      email: employee.email,
      store: employee.store,
      position: employee.position || "",
      payRate: employee.payRate,
      phone: employee.phone || "",
      address: employee.address || ""
    });
    setEditDialogOpen(true);
  };

  // Handle closing the edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEmployeeToEdit(null);
    setEditFormData({});
  };

  // Handle form field changes
  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // Handle saving employee edits
  const handleSaveEmployeeEdit = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }

      const res = await fetch(`${API_URL}/employees/${employeeToEdit.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`Server error: ${res.status} - ${errorData.error || res.statusText}`);
      }

      const updatedEmployee = await res.json();
      
      // Update the employee list
      setEmployeeList(prevList => 
        prevList.map(emp => emp.id === updatedEmployee.id ? {...emp, ...updatedEmployee} : emp)
      );

      // Show success message
      setSnackbarMessage("Employee information updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      // Close dialog
      handleCloseEditDialog();
    } catch (err) {
      console.error("Error updating employee:", err);
      setSnackbarMessage(`Error: ${err.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle update offer letter
  const handleUpdateOfferLetter = (employee) => {
    setEmployeeToUpdateOffer(employee);
    setUpdateOfferLetterOpen(true);
  };

  // Handle employee pay update
  const handleEmployeePay = (employee) => {
    setEmployeeToUpdatePay(employee);
    setEmployeePayOpen(true);
  };

  const handleOfferLetterUpdateSuccess = (result) => {
    setSnackbarMessage(`Offer letter updated successfully. ${result.emailSent ? 'Email notification sent.' : ''}`);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    
    // Refresh the notes if viewing this employee
    if (selectedEmployee && selectedEmployee.id === result.employeeId) {
      handleViewProfile(selectedEmployee);
    }
  };

  // Add new note (subject + content)
  const handleAddNote = async () => {
    if (newContent.trim() && selectedEmployee) {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/employees/${selectedEmployee.id}/notes`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ content: newContent, subject: newSubject }),
        });
        if (!res.ok) {
          throw new Error(`Server error: ${res.status}`);
        }
        const updated = await res.json();
        const withExpand = updated.map((n) => ({ ...n, expanded: false }));
        setNotes(withExpand);
        
        setNewSubject("");
        setNewContent("");
      } catch (err) {
        console.error("Error adding note:", err);
      }
    }
  };

  // Toggle expand for a single note
  const handleToggleNote = (noteId) => {
    setNotes((prev) =>
      prev.map((n) => {
        if (n.id === noteId) {
          return { ...n, expanded: !n.expanded };
        }
        return n;
      })
    );
  };

  // Expand All
  const handleExpandAll = () => {
    setNotes((prev) => prev.map((n) => ({ ...n, expanded: true })));
  };

  // Collapse All
  const handleCollapseAll = () => {
    setNotes((prev) => prev.map((n) => ({ ...n, expanded: false })));
  };

  const filteredEmployees = employeeList.filter((employee) => {
    const matchesStore = selectedStore === "All" || employee.store === selectedStore;
    const matchesSearch = searchQuery === "" || 
      employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Add employment status filtering - check current_employement_status
    const isArchived = employee.current_employement_status === 'Archived';
    const isActive = !isArchived;
    
    const matchesEmploymentStatus = 
      (employmentFilter === "active" && isActive) ||
      (employmentFilter === "archived" && isArchived);
    
    return matchesStore && matchesSearch && matchesEmploymentStatus;
  });

  // Desktop table sorting. Sorts the filtered list rather than the raw one, so
  // sorting composes with the store/search/status filters above. There is no
  // pagination here, so this orders the whole result set, not a page of it.
  const SORT_VALUE = {
    // firstName/lastName come straight from the API (employeeController SELECTs
    // both alongside `name`). Never split `name` on whitespace — surnames like
    // "Vargas Peral" and "Vidal Mejía" are two words.
    firstName: (e) => e.firstName,
    lastName: (e) => e.lastName,
    store: (e) => e.store,
    hireDate: (e) => (e.hireDate ? new Date(e.hireDate).getTime() : null),
    email: (e) => e.email,
    payRate: (e) => (e.payRate === null || e.payRate === '' ? null : Number(e.payRate)),
    status: (e) => (e.current_employement_status === 'Archived' ? 'Archived' : 'Active'),
  };

  const isBlank = (v) => v === null || v === undefined || v === '' || Number.isNaN(v);

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    const read = SORT_VALUE[sortBy];
    if (!read) return 0;
    const av = read(a);
    const bv = read(b);

    // Blanks always sink, in both directions — an empty cell is never the most
    // interesting row on the page.
    if (isBlank(av) && isBlank(bv)) return 0;
    if (isBlank(av)) return 1;
    if (isBlank(bv)) return -1;

    const order = sortDir === 'asc' ? 1 : -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * order;
  });

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const EmployeeStatusIcon = ({ employee }) => {
    if (employee.current_employement_status === 'Archived') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: 'grey.500' 
          }} />
          <Typography variant="caption" color="text.secondary">Archived</Typography>
        </Box>
      );
    }
    if (employee.fireDate) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Flame className="border-red-500 w-4 h-4" />
          <Typography variant="caption" color="error">Terminated</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Leaf className="border-green-500 w-4 h-4" />
        <Typography variant="caption" color="success.main">Active</Typography>
      </Box>
    );
  };
  const TableHeader = () => {
    const headers = [
      { label: "First Name", key: "firstName" },
      { label: "Last Name", key: "lastName" },
      { label: "Store", key: "store" },
      { label: "Hire Date", key: "hireDate" },
      { label: "Email", key: "email" },
    ];
    if (userRole === 'admin' || userRole === 'manager') {
      headers.push({ label: "Pay Rate", key: "payRate" });
    }
    headers.push({ label: "Status", key: "status" });
    headers.push({ label: "Profile", key: null });

    return (
      <TableRow sx={{ bgcolor: "#e0f7fa" }}>
        {headers.map(({ label, key }) => (
          <TableCell
            key={label}
            sortDirection={key && sortBy === key ? sortDir : false}
            sx={{
              fontWeight: "bold",
              color: "#00796b",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {key ? (
              <TableSortLabel
                active={sortBy === key}
                direction={sortBy === key ? sortDir : 'asc'}
                onClick={() => handleSort(key)}
                sx={{
                  fontWeight: "bold",
                  color: "#00796b",
                  '&.Mui-active': { color: "#00796b" },
                  '&.Mui-active .MuiTableSortLabel-icon': { color: "#00796b" },
                  '&:hover': { color: "#00796b" },
                }}
              >
                {label}
              </TableSortLabel>
            ) : (
              label
            )}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <Box sx={{ 
      position: "relative",
      zIndex: 1
    }}>
      <Box sx={{ 
        p: 2, 
        bgcolor: "#f5f5f5", 
        minHeight: "calc(100vh - 90px)", // Account for nav bar
        position: "relative",
        zIndex: 1
      }}>
        {/* Title + filters */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}
          >
            Employees
          </Typography>
          
          {/* Filters Container */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2, 
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            {/* Store Filter */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                label="Location"
              >
                <MenuItem value="All">All Locations</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.name}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Search Field */}
            <TextField
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ 
                minWidth: { xs: '100%', sm: 250 },
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1
                }
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    🔍
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Employment Status Filter */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={employmentFilter}
                onChange={(e) => setEmploymentFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="active">Active Employees</MenuItem>
                <MenuItem value="archived">Archived Employees</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {/* Action descriptions */}
          <Box sx={{ mt: 1, mb: 1 }}>
            {(userRole === 'admin' || userRole === 'manager') && (
              <Typography variant="caption" color="text.secondary" display="block">
                Pay Rate: Change position/hourly rate. Creates an acknowledgement for Admin review.
              </Typography>
            )}
            {canEditEmployee && (
              <Typography variant="caption" color="text.secondary" display="block">
                Update Offer Letter: Regenerate/reissue the offer letter with current details.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Table for non-mobile */}
        {!isMobile && (
          <TableContainer component={Paper} sx={{ mt: 4, boxShadow: 3, borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableHeader />
              </TableHead>
              <TableBody>
                {sortedEmployees.map((emp) => (
                  <TableRow
                    key={emp.id}
                    hover
                    sx={{
                      bgcolor: emp.current_employement_status === 'Archived' ? 'grey.50' : 'inherit',
                      opacity: emp.current_employement_status === 'Archived' ? 0.8 : 1
                    }}
                  >
                    <TableCell>{emp.firstName}</TableCell>
                    <TableCell>{emp.lastName}</TableCell>
                    <TableCell>{emp.store}</TableCell>
                    <TableCell>{dayjs(emp.hireDate).format("MM/DD/YYYY")}</TableCell>
                    <TableCell>{emp.email}</TableCell>
                    {(userRole === 'admin' || userRole === 'manager') && (
                      <TableCell>{formatPay(emp.payRate)}</TableCell>
                    )}
                    <TableCell>
                      <EmployeeStatusIcon employee={emp} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          onClick={() => handleViewProfile(emp)}
                          sx={{ textTransform: "none" }}
                        >
                          View Profile
                        </Button>
                        {canEditEmployee && emp.current_employement_status !== 'Archived' && (
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditEmployee(emp)}
                            sx={{ 
                              textTransform: "none",
                              bgcolor: '#276a78',
                              ml: 1,
                              borderRadius: '8px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              '&:hover': {
                                bgcolor: '#1d5761',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                              }
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Card layout for mobile */}
        {isMobile && (
          <Box>
            {filteredEmployees.map((emp) => (
              <Card 
                key={emp.id} 
                sx={{ 
                  mb: 2,
                  bgcolor: emp.current_employement_status === 'Archived' ? 'grey.50' : 'background.paper',
                  opacity: emp.current_employement_status === 'Archived' ? 0.8 : 1,
                  border: emp.current_employement_status === 'Archived' ? '1px solid' : 'none',
                  borderColor: 'grey.300'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {emp.name}
                    </Typography>
                    <EmployeeStatusIcon employee={emp} />
                  </Box>
                  <Typography variant="body1">Store: {emp.store}</Typography>
                  <Typography variant="body1">Hire Date: {dayjs(emp.hireDate).format("MM/DD/YYYY")}</Typography>
                  <Typography variant="body1">Email: {emp.email}</Typography>
                  {userRole === 'admin' && (
                    <Typography variant="body1">Pay Rate: {formatPay(emp.payRate)}</Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleViewProfile(emp)}
                    >
                      View Profile
                    </Button>
                    {canEditEmployee && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditEmployee(emp)}
                        sx={{ 
                          textTransform: "none",
                          bgcolor: '#276a78',
                          ml: 1,
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': {
                            bgcolor: '#1d5761',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Drawer for employee profile */}
        <Drawer
  anchor={isMobile ? "bottom" : "right"}
  open={isProfileOpen}
  onClose={handleCloseProfile}
  variant="temporary"
  ModalProps={{
    keepMounted: true, // Better performance for mobile
    sx: {
      '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(3px)',
        zIndex: 1300
      },
    },
  }}
  sx={{
    zIndex: 1400, // Higher than any other component
    position: 'fixed'
  }}
  PaperProps={{
    sx: {
      height: isMobile ? "90%" : "100%",
      width: isMobile ? "100%" : "50%",
      borderTopLeftRadius: isMobile ? "16px" : 0,
      borderTopRightRadius: isMobile ? "16px" : 0,
      p: 0,
      fontFamily: "Poppins, sans-serif",
      overflow: 'hidden',
      position: 'absolute',
      zIndex: 1400,
      marginTop: '0 !important', // Override any margin that might be applied
    },
  }}
>
  {selectedEmployee && (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky Header */}
      <Box
  sx={{
    position: 'sticky',
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)', // Transparent white background
    backdropFilter: 'blur(8px)', // Frosted glass effect
    WebkitBackdropFilter: 'blur(8px)', // Safari compatibility
    zIndex: 1,
    borderBottom: '1px solid rgba(224, 224, 224, 0.8)', // Semi-transparent border
    p: 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }}
>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      {selectedEmployee.firstName} {selectedEmployee.lastName}
    </Typography>
    {selectedEmployee.current_employement_status === 'Archived' && (
      <Chip 
        label="ARCHIVED" 
        size="small" 
        sx={{ 
          bgcolor: 'grey.600', 
          color: 'white',
          fontWeight: 'bold'
        }} 
      />
    )}
  </Box>
  <IconButton onClick={handleCloseProfile}>
    <CloseIcon />
  </IconButton>
</Box>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        p: 3,
      }}>
        {/* Privacy Notice for Archived Employees */}
        {selectedEmployee.current_employement_status === 'Archived' && userRole !== 'admin' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This employee has been archived. Personal information is protected and only accessible to administrators.
            </Typography>
          </Alert>
        )}
        
        {/* Employee Details */}
        <Box
          sx={{
            border: "1px solid #d1d1d1",
            mt: 2,
            p: 2,
            bgcolor: "#e3f2fd",
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BadgeIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Employee Details
              </Typography>
            </Box>
            {/* Action buttons moved to Money section */}
          </Box>
          <Typography>
            <strong>Position:</strong> {selectedEmployee.position}
          </Typography>
          <Typography>
            <strong>Store:</strong> {selectedEmployee.store}
          </Typography>
          <Typography>
            <strong>Hire Date:</strong> {dayjs(selectedEmployee.hireDate).format("MM/DD/YYYY")}
          </Typography>
          <Typography>
            <strong>Email:</strong> {selectedEmployee.email}
          </Typography>
          <Typography>
            <strong>Phone:</strong> {selectedEmployee.phone}
          </Typography>
          <Typography>
            <strong>Address:</strong> {selectedEmployee.address}
          </Typography>
          {(userRole === 'admin' || userRole === 'manager') && (
            <Typography>
              <strong>Pay Rate: </strong>{formatPay(selectedEmployee.payRate)}
            </Typography>
          )}
          
          {/* Social Security Number - Only visible to admins */}
          {userRole === 'admin' && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
              <BadgeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography sx={{ flex: 1 }}>
                <strong>SSN:</strong> {showSSN ? (selectedEmployee.ssn || "Not provided") : maskSensitiveData(selectedEmployee.ssn, 'ssn')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowSSN(!showSSN)}
                sx={{ ml: 1 }}
              >
                {showSSN ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Money Section */}
        {(selectedEmployee.current_employement_status !== 'Archived') && (
          <Box
            sx={{
              border: "1px solid #d1d1d1",
              mt: 2,
              p: 2,
              bgcolor: "#eef7f3",
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <MonetizationOnIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Money
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {canEditEmployee && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => handleUpdateOfferLetter(selectedEmployee)}
                  sx={{ textTransform: 'none' }}
                >
                  Update Offer Letter
                </Button>
              )}
              {(userRole === 'admin' || userRole === 'manager') && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<MonetizationOnIcon />}
                  onClick={() => handleEmployeePay(selectedEmployee)}
                  sx={{ textTransform: 'none' }}
                >
                  Employee Pay
                </Button>
              )}
            </Box>
            <Box>
              {(userRole === 'admin' || userRole === 'manager') && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Pay Rate: Change position/hourly rate. Creates an acknowledgement for Admin review.
                </Typography>
              )}
              {canEditEmployee && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Update Offer Letter: Regenerate/reissue the offer letter with current details.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Direct Deposit Information - Only visible to admins */}
        {userRole === 'admin' && (
          <Box
            sx={{
              border: "1px solid #d1d1d1",
              mt: 2,
              p: 2,
              bgcolor: "#f3e5f5",
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <AccountBalanceIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Direct Deposit Information
              </Typography>
            </Box>
            
            <Typography>
              <strong>Bank Name:</strong> {selectedEmployee.bankName || "Not provided"}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
              <Typography sx={{ flex: 1 }}>
                <strong>Account Number:</strong> {showBankAccount ? (selectedEmployee.accountNumber || "Not provided") : maskSensitiveData(selectedEmployee.accountNumber, 'account')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowBankAccount(!showBankAccount)}
              >
                {showBankAccount ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
              <Typography sx={{ flex: 1 }}>
                <strong>Routing Number:</strong> {showRoutingNumber ? (selectedEmployee.routingNumber || "Not provided") : maskSensitiveData(selectedEmployee.routingNumber, 'routing')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowRoutingNumber(!showRoutingNumber)}
              >
                {showRoutingNumber ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Box>
            
            <Typography sx={{ mt: 1 }}>
              <strong>Account Type:</strong> {selectedEmployee.accountType || "Not specified"}
            </Typography>
          </Box>
        )}

        {/* Emergency Contacts */}
        {selectedEmployee.emergencyContacts && selectedEmployee.emergencyContacts.length > 0 && (
          <Box
            sx={{
              border: "1px solid #d1d1d1",
              mt: 2,
              p: 2,
              bgcolor: "#fff3e0",
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <ContactPhoneIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Emergency Contacts
              </Typography>
            </Box>
            {selectedEmployee.emergencyContacts.map((contact, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, borderBottom: index < selectedEmployee.emergencyContacts.length - 1 ? "1px solid #ddd" : "none" }}>
                <Typography>
                  <strong>Name:</strong> {contact.name}
                </Typography>
                <Typography>
                  <strong>Relationship:</strong> {contact.relationship}
                </Typography>
                <Typography>
                  <strong>Phone:</strong> {contact.phone}
                </Typography>
                <Typography>
                  <strong>Address:</strong> {contact.address}
                </Typography>
              </Box>
            ))}
          </Box>
          
        )}

        {/* Onboarding Letters */}
        {selectedEmployee.forms && selectedEmployee.forms.length > 0 && (
          
          
          <Box
            sx={{
              border: "1px solid #d1d1d1",
              mt: 2,
              p: 2,
              bgcolor: "#f1f8e9",
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <DescriptionIcon />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Onboarding Letters
              </Typography>
            </Box>
            <List>
              {selectedEmployee.forms.map((form) => (
                <ListItem key={form.formId}>
                  <ListItemIcon>
                    {form.isCompleted ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <HourglassEmptyIcon color="warning" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={form.formName} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Notes Section */}
        <Box
          sx={{
            mb: 10,
            border: "1px solid #d1d1d1",
            mt: 2,
            p: 2,
            bgcolor: "#e3f2fd",
            borderRadius: 2,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            Notes
          </Typography>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 2 }}>
            <Button variant="outlined" onClick={handleExpandAll}>
              Expand All
            </Button>
            <Button variant="outlined" onClick={handleCollapseAll}>
              Collapse All
            </Button>
          </Box>

          {/* Notes List */}
          {notes.map((note) => {
            const dateString = note.noteDate
              ? dayjs(note.noteDate).format("MMM D, YYYY")
              : dayjs(note.createdAt).format("MMM D, YYYY");

            return (
              <Accordion
                key={note.id}
                expanded={note.expanded}
                onChange={() => handleToggleNote(note.id)}
                sx={{ 
                  mb: 2, // Add spacing between notes
                  boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                  '&:before': {
                    display: 'none', // Remove the default divider line
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'rgba(41, 121, 138, 0.03)', // Light blue background
                    '&:hover': {
                      backgroundColor: 'rgba(41, 121, 138, 0.06)',
                    }
                  }}
                >
                  <Typography>
                    {note.subject ? note.subject + " — " : ""}
                    {dateString}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                  <Typography variant="body2" sx={{ fontStyle: "italic", mb: 1 }}>
                    {note.subject || "(No subject)"}
                  </Typography>
                  <Typography>{note.content}</Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Add New Note */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Add a New Note
          </Typography>
          <TextField
            label="Subject (optional)"
            fullWidth
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            sx={{ mb: 2, backgroundColor: "white" }}
          />
          <TextField
            label="Note Content"
            multiline
            rows={3}
            fullWidth
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            sx={{ mb: 2, backgroundColor: "white" }}
          />
          <Button variant="contained" onClick={handleAddNote}>
            Add Note
          </Button>
        </Box>

        {/* Archive — the last thing in the profile panel, so it is findable
            without being adjacent to everyday edit controls. */}
        {canEditEmployee && selectedEmployee.current_employement_status !== 'Archived' && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              border: '1px solid',
              borderColor: 'warning.light',
              borderRadius: 2,
              bgcolor: 'rgba(255, 152, 0, 0.06)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Archive this employee
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use this when {selectedEmployee.firstName} no longer works here. They stop
              appearing in the active roster and are no longer counted on your monthly
              invoice. Their records are kept, and you can reinstate them later.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              startIcon={<ArchiveIcon />}
              onClick={handleOpenArchiveDialog}
              fullWidth={isMobile}
            >
              Archive Employee
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )}
</Drawer>
      </Box>

      {/* Edit Employee Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            maxHeight: '90vh',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#276a78',
            color: 'white',
            py: 2,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            fontSize: '1.2rem',
            fontWeight: 600
          }}
        >
          Edit Employee Information
          <IconButton
            aria-label="close"
            onClick={handleCloseEditDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            bgcolor: '#f9fafb'
          }}
        >
          {employeeToEdit && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label="Employee Name"
                  fullWidth
                  value={editFormData.name || ''}
                  onChange={handleEditFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  value={editFormData.email || ''}
                  onChange={handleEditFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Store</InputLabel>
                  <Select
                    name="store"
                    value={editFormData.store || ''}
                    onChange={handleEditFormChange}
                    label="Store"
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.name}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="position"
                  label="Position"
                  fullWidth
                  value={editFormData.position || ''}
                  onChange={handleEditFormChange}
                />
              </Grid>
              {/* Pay Rate is edited via Employee Pay panel only */}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label="Phone Number"
                  fullWidth
                  value={editFormData.phone || ''}
                  onChange={handleEditFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Address"
                  fullWidth
                  multiline
                  rows={2}
                  value={editFormData.address || ''}
                  onChange={handleEditFormChange}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 2.5, 
            bgcolor: '#f9fafb',
            borderTop: '1px solid #e0e0e0'
          }}
        >
          <Button 
            onClick={handleCloseEditDialog} 
            color="primary"
            variant="outlined"
            sx={{
              color: '#276a78', 
              borderColor: '#276a78',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#1d5761',
                bgcolor: 'rgba(39, 106, 120, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEmployeeEdit} 
            color="primary" 
            variant="contained"
            sx={{
              ml: 2,
              bgcolor: '#276a78',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                bgcolor: '#1d5761',
                boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Offer Letter Dialog */}
      {updateOfferLetterOpen && employeeToUpdateOffer && (
        <UpdateOfferLetter
          employee={employeeToUpdateOffer}
          locations={locations}
          onClose={() => {
            setUpdateOfferLetterOpen(false);
            setEmployeeToUpdateOffer(null);
          }}
          onSuccess={handleOfferLetterUpdateSuccess}
        />
      )}

      {/* Employee Pay Dialog */}
      {employeePayOpen && employeeToUpdatePay && (
        <EmployeePay
          employee={employeeToUpdatePay}
          onClose={() => {
            setEmployeePayOpen(false);
            setEmployeeToUpdatePay(null);
          }}
          onSuccess={(result) => {
            const { message, updated } = typeof result === 'string' ? { message: result, updated: null } : result;
            setSnackbarMessage(message);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            // Immediate UI update without full refresh
            if (updated) {
              // Update list
              setEmployeeList(prev => prev.map(emp => 
                emp.id === employeeToUpdatePay.id 
                  ? { ...emp, position: updated.position, payRate: updated.payRate }
                  : emp
              ));
              // Update selected profile view if open
              setSelectedEmployee(prev => prev && prev.id === employeeToUpdatePay.id 
                ? { ...prev, position: updated.position, payRate: updated.payRate }
                : prev
              );
            } else {
              // Fallback to refresh list
              fetchEmployees();
            }
            setEmployeePayOpen(false);
            setEmployeeToUpdatePay(null);
          }}
        />
      )}

      {/* Archive confirmation — reason is required by the API, so ask for it
          here rather than letting the request fail validation. */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => !archiveSubmitting && setArchiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Archive {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : "employee"}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            They will be moved out of the active roster and will stop being counted on
            your monthly invoice. Nothing is deleted, and you can reinstate them later.
          </Typography>
          <TextField
            label="Last day worked"
            type="date"
            fullWidth
            value={archiveDate}
            onChange={(e) => setArchiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Reason"
            fullWidth
            required
            multiline
            rows={2}
            placeholder="e.g. Resigned, end of season, no longer with the company"
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value)}
            helperText="Required. Stored with the employee record."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiveSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ArchiveIcon />}
            onClick={handleArchiveEmployee}
            disabled={archiveSubmitting || !archiveReason.trim()}
          >
            {archiveSubmitting ? "Archiving…" : "Archive Employee"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Employees;
